import { PlaceholderPreviewTooltip } from '@/components/campaigns/simulations/PlaceholderPreviewTooltip';
import { RichTextEditor, RichTextEditorHandle } from '@/ui/rich-text';
import classNames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import Quill, { RangeStatic } from 'quill';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import './ai-email-blots';
import { getBodyStats, isChangeRejected } from './ai-email-body-stats';
import {
  AI_EMAIL_LINK_CLASS,
  AI_EMAIL_LINK_FORMAT,
  AI_EMAIL_PLACEHOLDER_CLASS,
  AI_EMAIL_PLACEHOLDER_FORMAT,
  AI_EMAIL_SENDER_CLASS,
  AI_EMAIL_SENDER_FORMAT,
  AI_EMAIL_SOFT_BREAK_CLASS,
  AI_EMAIL_SOFT_BREAK_FORMAT,
  AI_EMAIL_SUBJECT_MAX_LENGTH,
} from './ai-email-editor.constants';
import { AiEmailDraft } from './ai-email-html.helper';

const EDITOR_FORMATS = [
  'bold',
  'italic',
  'underline',
  'list',
  AI_EMAIL_PLACEHOLDER_FORMAT,
  AI_EMAIL_LINK_FORMAT,
  AI_EMAIL_SENDER_FORMAT,
  AI_EMAIL_SOFT_BREAK_FORMAT,
];

const PASTE_INLINE_FORMATS = [
  'bold',
  'italic',
  'underline',
  AI_EMAIL_LINK_FORMAT,
  AI_EMAIL_SENDER_FORMAT,
];

const PILL_SELECTOR = `.${AI_EMAIL_PLACEHOLDER_CLASS}, .${AI_EMAIL_LINK_CLASS}, .${AI_EMAIL_SENDER_CLASS}`;

const PILL_TEXT_FORMATS = [AI_EMAIL_LINK_FORMAT, AI_EMAIL_SENDER_FORMAT];

const NO_FORMAT_SHORTCUT_KEYS = ['B', 'I', 'U'];
const SPACE_KEY_CODE = 32;
const ENTER_KEY_CODE = 13;

type PillKind = 'name' | 'link' | 'sender';

type HoveredPill = { element: HTMLElement; kind: PillKind; id: number };

type KeyboardBinding = { prefix?: unknown };

type KeyboardContext = { format: Record<string, unknown> };

type QuillWithModules = Quill & {
  history: { clear: () => void };
  keyboard: { bindings: Record<number, KeyboardBinding[]> };
};

const TOOLTIP_KEYS: Record<PillKind, string> = {
  name: 'phishing.simulations.modal.aiSimulation.editor.tooltip.recipientName',
  link: 'phishing.simulations.modal.aiSimulation.editor.tooltip.link',
  sender: 'phishing.simulations.modal.aiSimulation.editor.tooltip.senderName',
};

const getPillKind = (element: HTMLElement): PillKind => {
  if (element.classList.contains(AI_EMAIL_LINK_CLASS)) return 'link';
  if (element.classList.contains(AI_EMAIL_SENDER_CLASS)) return 'sender';
  return 'name';
};

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
  // Pull the input chrome (padding + 1px border) outward so the text lines up with the tag, as in Figma
  margin-inline: calc(-1 * var(--xsmall-padding) - 1px);
`;

const SubjectInput = styled.input`
  width: 100%;
  font-size: var(--medium-font-size);
  font-weight: 700;
  color: var(--black-main);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--xxxsmall-border-radius);
  padding: var(--xxxsmall-padding) var(--xsmall-padding);

  &:hover,
  &:focus {
    border-color: var(--red-main);
    outline: none;
  }
`;

const EditorWrapper = styled.div`
  .quill {
    min-height: 0;
    height: auto;
  }

  .quill .ql-container {
    padding: 0;
  }

  .quill .ql-container .ql-editor {
    font-size: var(--small-font-size);
    line-height: var(--default-line-height);
    color: var(--black-main);
    padding: var(--xsmall-padding);
  }

  .quill .ql-container .ql-editor p {
    margin: 0 0 1em;
  }

  .quill .ql-container .ql-editor p:last-child {
    margin-bottom: 0;
  }

  .ql-editor
    .${AI_EMAIL_PLACEHOLDER_CLASS},
    .ql-editor
    .${AI_EMAIL_LINK_CLASS},
    .ql-editor
    .${AI_EMAIL_SENDER_CLASS} {
    font-size: var(--xsmall-font-size);
    font-weight: var(--medium-font-weight);
    line-height: var(--xsmall-line-height);
    padding: var(--xxxsmall-padding) var(--xsmall-padding);
    border-radius: var(--grand-border-radius);
    background: var(--platinum-main);
    color: var(--gray-darkest);
    text-decoration: none;
    white-space: normal;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  .ql-editor .${AI_EMAIL_PLACEHOLDER_CLASS} {
    cursor: default;
  }

  .ql-editor .${AI_EMAIL_LINK_CLASS}, .ql-editor .${AI_EMAIL_SENDER_CLASS} {
    cursor: text;
  }
`;

const TooltipContent = styled.div`
  pointer-events: none;
  max-width: 320px;
  white-space: normal;
`;

type AiEmailEditorProps = {
  draft: AiEmailDraft;
  onDraftChange: (update: (previous: AiEmailDraft) => AiEmailDraft) => void;
};

export const AiEmailEditor: React.FC<AiEmailEditorProps> = ({
  draft,
  onDraftChange,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<RichTextEditorHandle>(null);
  const hoverIdRef = useRef(0);
  const [isFocused, setIsFocused] = useState(false);
  const [hovered, setHovered] = useState<HoveredPill | null>(null);
  const [tooltipZIndex, setTooltipZIndex] = useState(1000);

  const anchorRef = useMemo(
    () => ({ current: hovered?.element ?? null }),
    [hovered],
  );

  useEffect(() => {
    const editor = editorRef.current?.getVendorEditor() as Quill | null;
    if (!editor) return;
    const quill = editor as QuillWithModules;
    const Delta = Quill.import('delta');
    const Parchment = Quill.import('parchment');

    editor.clipboard.addMatcher('BR', (node, delta) =>
      (node as HTMLElement).classList.contains(AI_EMAIL_SOFT_BREAK_CLASS)
        ? new Delta().insert({ [AI_EMAIL_SOFT_BREAK_FORMAT]: true })
        : delta,
    );

    editor.setContents(
      editor.clipboard.convert(draft.editorBodyHtml),
      'silent',
    );
    quill.history.clear();
    let lastAccepted = editor.getContents();
    let lastRange: RangeStatic | null = null;
    let lastChangeRejected = false;
    editor.root.setAttribute(
      'aria-label',
      t('phishing.simulations.modal.aiSimulation.editor.body'),
    );

    NO_FORMAT_SHORTCUT_KEYS.forEach((key) => {
      editor.keyboard.addBinding({ key, shortKey: true }, () => false);
      const list = quill.keyboard.bindings[key.charCodeAt(0)];
      const binding = list.pop();
      if (binding) list.unshift(binding);
    });

    const pillEndEnterBinding = {
      key: 'Enter',
      collapsed: true,
      format: PILL_TEXT_FORMATS,
    };
    editor.keyboard.addBinding(
      pillEndEnterBinding,
      (range: RangeStatic, context: KeyboardContext) => {
        const nextFormats = editor.getFormat(range.index, 1);
        if (PILL_TEXT_FORMATS.some((name) => nextFormats[name])) return true;

        const lineFormats = Object.fromEntries(
          Object.entries(context.format).filter(
            ([name, value]) =>
              Parchment.query(name, Parchment.Scope.BLOCK) &&
              !Array.isArray(value),
          ),
        );
        editor.insertText(range.index, '\n', lineFormats, 'user');
        editor.setSelection(range.index + 1, 0, 'silent');
        editor.focus();
        Object.entries(context.format).forEach(([name, value]) => {
          if (name in lineFormats || Array.isArray(value)) return;
          if (PILL_TEXT_FORMATS.includes(name)) return;
          editor.format(name, value, 'user');
        });
        return false;
      },
    );
    const enterBindings = quill.keyboard.bindings[ENTER_KEY_CODE];
    const enterBinding = enterBindings.pop();
    if (enterBinding) enterBindings.unshift(enterBinding);

    quill.keyboard.bindings[SPACE_KEY_CODE] = quill.keyboard.bindings[
      SPACE_KEY_CODE
    ].filter(
      (binding) =>
        !(
          binding.prefix instanceof RegExp &&
          binding.prefix.source.includes('\\d+\\.')
        ),
    );

    editor.root.addEventListener(
      'paste',
      (event: ClipboardEvent) => {
        event.preventDefault();
        const text = (event.clipboardData?.getData('text/plain') ?? '').replace(
          /\r\n/g,
          '\n',
        );
        const range = editor.getSelection(true);
        const currentFormats = editor.getFormat(range.index, 0);
        const inlineFormats = Object.fromEntries(
          Object.entries(currentFormats).filter(([name]) =>
            PASTE_INLINE_FORMATS.includes(name),
          ),
        );
        editor.updateContents(
          new Delta()
            .retain(range.index)
            .delete(range.length)
            .insert(text, inlineFormats),
          'user',
        );
        editor.setSelection(range.index + text.length, 0, 'silent');
      },
      true,
    );

    editor.root.addEventListener(
      'beforeinput',
      (event: InputEvent) => {
        const range = editor.getSelection();
        if (
          event.inputType !== 'insertText' ||
          !event.data ||
          !range ||
          range.length === 0
        ) {
          return;
        }
        event.preventDefault();
        const { data } = event;
        const typedFormats = Object.fromEntries(
          Object.entries(editor.getFormat(range.index, 1)).filter(([name]) =>
            PASTE_INLINE_FORMATS.includes(name),
          ),
        );
        lastChangeRejected = false;
        editor.updateContents(
          new Delta()
            .retain(range.index)
            .delete(range.length)
            .insert(data, typedFormats),
          'user',
        );
        if (lastChangeRejected) return;
        lastRange = { index: range.index + data.length, length: 0 };
        editor.setSelection(lastRange, 'silent');
      },
      true,
    );

    const preventDefault = (event: Event) => event.preventDefault();
    editor.root.addEventListener('dragstart', preventDefault);
    editor.root.addEventListener('drop', preventDefault);

    editor.on('selection-change', (range) => {
      if (range) lastRange = range;
    });

    editor.on('text-change', (_delta, _oldContents, source) => {
      if (source !== 'user') return;
      setHovered(null);

      const stats = getBodyStats(editor.getContents());
      if (isChangeRejected(stats, getBodyStats(lastAccepted))) {
        lastChangeRejected = true;
        editor.setContents(lastAccepted, 'silent');
        const restoredRange = lastRange;
        if (restoredRange) {
          editor.setSelection(restoredRange, 'silent');
          Promise.resolve().then(() =>
            editor.setSelection(restoredRange, 'silent'),
          );
        }
        quill.history.clear();
        onDraftChange((previous) => ({ ...previous, bodyTouched: true }));
        return;
      }

      lastAccepted = editor.getContents();
      lastRange = editor.getSelection() ?? lastRange;
      const editorBodyHtml = editor.root.innerHTML;
      onDraftChange((previous) => ({
        ...previous,
        editorBodyHtml,
        bodyText: stats.bodyText,
        senderName: stats.senderRuns[0]?.text,
        linkTexts: stats.linkRuns.map((run) => run.text),
        bodyTouched: true,
        bodyEdited: true,
      }));
    });
  }, []);

  const handleMouseOver = (event: React.MouseEvent<HTMLDivElement>) => {
    const pill = (event.target as HTMLElement).closest<HTMLElement>(
      PILL_SELECTOR,
    );
    if (!pill || pill === hovered?.element) return;

    const mask = rootRef.current?.closest('.p-dialog-mask');
    setTooltipZIndex(mask ? Number(getComputedStyle(mask).zIndex) + 1 : 1000);
    hoverIdRef.current += 1;
    setHovered({
      element: pill,
      kind: getPillKind(pill),
      id: hoverIdRef.current,
    });
  };

  const handleMouseOut = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!hovered) return;
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && hovered.element.contains(relatedTarget)) return;
    setHovered(null);
  };

  return (
    <EditorContainer
      ref={rootRef}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <SubjectInput
        type="text"
        defaultValue={draft.subject}
        autoFocus
        maxLength={AI_EMAIL_SUBJECT_MAX_LENGTH}
        aria-label={t('phishing.simulations.modal.aiSimulation.editor.subject')}
        onChange={(event) => {
          const { value } = event.target;
          onDraftChange((previous) => ({
            ...previous,
            subject: value,
            subjectTouched: true,
          }));
        }}
      />
      <EditorWrapper>
        <RichTextEditor
          ref={editorRef}
          formats={EDITOR_FORMATS}
          className={classNames({ 'p-editor-focused': isFocused })}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </EditorWrapper>
      {hovered && (
        <PlaceholderPreviewTooltip
          key={hovered.id}
          anchorRef={anchorRef}
          zIndex={tooltipZIndex}
        >
          <TooltipContent>{t(TOOLTIP_KEYS[hovered.kind])}</TooltipContent>
        </PlaceholderPreviewTooltip>
      )}
    </EditorContainer>
  );
};
